import * as React from 'react'
import styled from 'styled-components'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { otaPendingReservationService, utilService } from '../../../../core/services'
import { IOtaPendingReservation } from '../../../../core/services/OtaPendingReservationService'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import {
  hasAtLeastOnePerson,
  NUM_OF_PEOPLE_ERROR_MESSAGE,
  toNonNegativeInteger,
} from '../../../../utils/otaPendingReservation'
import { Button, Select, InputText, InputTextArea } from '../../../atoms'
import SidebarLayout from 'components/pages/_layouts/sidebarLayout'
import * as constants from '../../../../static/constants'
import {
  OTA_PENDING_STATUS_LABELS,
  OTA_PENDING_STATUS_OPTIONS,
  normalizeOtaPendingStatus,
  OTA_PENDING_PROCESS_ACTION_OPTIONS,
  getStatusBadgeBackgroundColor,
  getStatusBadgeTextColor,
  CHANNEL_SELLER_OPTIONS,
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
} from '../../../../static/constants_ota_pending'
import AgentLayoutIndex from '../_layouts'

declare var window: interfaces.IWindow

interface IProps {
  user: {
    users: IJsonResponse
    meta: interfaces.IPager
  }
  ota_pending_reservation: {
    data: IJsonResponse
  }
}

// 動的に選択肢を生成（既存値が標準選択肢にない場合は追加）
const getOptionsWithCurrentValue = (
  options: Array<{ value: string; label: string }>,
  currentValue: string | undefined
) => {
  if (!currentValue) {
    return options
  }
  const exists = options.some(opt => opt.value === currentValue)
  if (exists) {
    return options
  }
  return [{ value: currentValue, label: currentValue }, ...options]
}

const AgentOtaPendingReservationEdit: React.FC<IProps> = props => {
  const { data: user } = utilService.getDataFromJson(props.user)
  const initialData = otaPendingReservationService.getDataFromJson(props.ota_pending_reservation)
    .data as IOtaPendingReservation

  Hooks.SetDocumentTitle(`手動入力 #${initialData.id}`)

  const [reservation, setReservation] = React.useState<IOtaPendingReservation>(initialData)
  const [savedReservation, setSavedReservation] = React.useState<IOtaPendingReservation>(
    initialData
  )
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [isProcessing, setIsProcessing] = React.useState(false)
  const [errors, setErrors] = React.useState<Record<string, string[]>>({})

  // 開始時刻を時と分に分解
  const [startHour, setStartHour] = React.useState(() => {
    const time = initialData.tour_start_at || ''
    const match = time.match(/^(\d{2}):(\d{2})/)
    return match ? match[1] : '09'
  })
  const [startMinute, setStartMinute] = React.useState(() => {
    const time = initialData.tour_start_at || ''
    const match = time.match(/^(\d{2}):(\d{2})/)
    return match ? match[2] : '00'
  })

  // 時刻が変更されたらreservationを更新
  React.useEffect(() => {
    setReservation(prev => ({
      ...prev,
      tour_start_at: `${startHour}:${startMinute}`,
    }))
  }, [startHour, startMinute])

  const handleChange = React.useCallback(
    (field: keyof IOtaPendingReservation) => (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
      const value = e.target.value
      setReservation(prev => ({
        ...prev,
        [field]: value,
      }))
    },
    []
  )

  const handleSubmit = React.useCallback(async () => {
    if (
      !hasAtLeastOnePerson(
        reservation.num_of_adults,
        reservation.num_of_children,
        reservation.num_of_infants
      )
    ) {
      setErrors({ num_of_people: [NUM_OF_PEOPLE_ERROR_MESSAGE] })
      window.flashMessages.addMessage({ text: '入力内容にエラーがあります', type: 'error' })
      return
    }

    try {
      setIsSubmitting(true)
      setErrors({})

      const {
        ota_pending_reservation,
        flush,
      } = await otaPendingReservationService.updateOtaPendingReservation(reservation.id, {
        status: reservation.status,
        channel_manager: reservation.channel_manager,
        seller: reservation.seller,
        seller_booking_id: reservation.seller_booking_id,
        process_action: reservation.process_action,
        customer_first_name: reservation.customer_first_name,
        customer_last_name: reservation.customer_last_name,
        customer_email: reservation.customer_email,
        customer_phone: reservation.customer_phone,
        customer_country: reservation.customer_country,
        num_of_adults: reservation.num_of_adults,
        num_of_children: reservation.num_of_children,
        num_of_infants: reservation.num_of_infants,
        tour_product_id: reservation.tour_product_id,
        tour_title: reservation.tour_title,
        tour_date: reservation.tour_date,
        tour_start_at: reservation.tour_start_at,
        currency: 'JPY',
        total_price: reservation.total_price,
        other_requests: reservation.other_requests,
      })

      const nextReservation = ota_pending_reservation as IOtaPendingReservation
      setReservation(nextReservation)
      setSavedReservation(nextReservation)

      if (flush) {
        window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      }
    } catch (error) {
      const err = error as any
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors)
        window.flashMessages.addMessage({
          text: '入力内容にエラーがあります',
          type: 'error',
        })
      } else {
        window.flashMessages.addMessage({
          text: '更新に失敗しました',
          type: 'error',
        })
      }
    } finally {
      setIsSubmitting(false)
    }
  }, [reservation])

  const handleProcess = React.useCallback(async () => {
    if (!window.confirm('この予約をota_reservationsに登録しますか？')) {
      return
    }

    try {
      setIsProcessing(true)
      setErrors({})

      const {
        ota_pending_reservation,
        ota_reservation,
        flush,
      } = await otaPendingReservationService.processReservation(reservation.id)

      setReservation(ota_pending_reservation as IOtaPendingReservation)

      if (flush) {
        window.flashMessages.addMessage({ text: flush.message, type: flush.type })
      }

      if (ota_reservation) {
        window.flashMessages.addMessage({
          text: `OTA予約 #${ota_reservation.id} を作成しました`,
          type: 'success',
        })
      }
    } catch (error) {
      const err = error as any
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors)
      }
      window.flashMessages.addMessage({
        text: '処理に失敗しました',
        type: 'error',
      })
    } finally {
      setIsProcessing(false)
    }
  }, [reservation.id])

  const handleBack = React.useCallback(() => {
    window.location.href = '/agent/ota_pending_reservations'
  }, [])

  const buildComparableValues = React.useCallback(
    (target: IOtaPendingReservation) => ({
      status: Number(target.status),
      channel_manager: target.channel_manager || '',
      seller: target.seller || '',
      seller_booking_id: target.seller_booking_id || '',
      process_action: target.process_action || '',
      customer_first_name: target.customer_first_name || '',
      customer_last_name: target.customer_last_name || '',
      customer_email: target.customer_email || '',
      customer_phone: target.customer_phone || '',
      customer_country: target.customer_country || '',
      num_of_adults: toNonNegativeInteger(target.num_of_adults || 0),
      num_of_children: toNonNegativeInteger(target.num_of_children || 0),
      num_of_infants: toNonNegativeInteger(target.num_of_infants || 0),
      tour_product_id: target.tour_product_id || '',
      tour_title: target.tour_title || '',
      tour_date: target.tour_date || '',
      tour_start_at: target.tour_start_at || '',
      total_price: toNonNegativeInteger(target.total_price || 0),
      other_requests: target.other_requests || '',
    }),
    []
  )

  const isDirty = React.useMemo(() => {
    return (
      JSON.stringify(buildComparableValues(reservation)) !==
      JSON.stringify(buildComparableValues(savedReservation))
    )
  }, [reservation, savedReservation, buildComparableValues])

  const normalizedStatus = normalizeOtaPendingStatus(reservation.status)
  const statusLabel = OTA_PENDING_STATUS_LABELS[normalizedStatus] || String(reservation.status)
  const isProcessed = normalizedStatus === 3
  const canProcess = reservation.processable && !isProcessed
  const showUnsavedNotice = canProcess && isDirty

  // 動的選択肢
  const channelManagerOptions = getOptionsWithCurrentValue(
    CHANNEL_SELLER_OPTIONS,
    reservation.channel_manager
  )
  const sellerOptions = getOptionsWithCurrentValue(CHANNEL_SELLER_OPTIONS, reservation.seller)

  return (
    <SidebarLayout user={user}>
      <Wrapper>
        <AgentLayoutIndex
          hideAgentNav={true}
          title={
            <Heading>
              <span>手動入力 #{reservation.id}</span>
              <StatusBadge status={reservation.status}>{statusLabel}</StatusBadge>
            </Heading>
          }
          model={'ota_pending_reservations'}
          main={
            <FormWrapper>
              {/* ステータス */}
              <Section>
                <SectionTitle>ステータス</SectionTitle>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>ステータス</FieldLabel>
                    <Select
                      name="status"
                      options={OTA_PENDING_STATUS_OPTIONS}
                      value={String(normalizedStatus)}
                      onChangeHandler={handleChange('status')}
                    />
                    {errors.status && <ErrorText>{errors.status.join(', ')}</ErrorText>}
                  </FieldGroup>
                </FieldRow>
                {reservation.ota_reservation_id && (
                  <FieldRow>
                    <FieldGroup>
                      <FieldLabel>処理済みOTA予約ID</FieldLabel>
                      <LinkedValue>
                        <a
                          href={`/agent/ota_reservations/${reservation.ota_reservation_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          #{reservation.ota_reservation_id}
                        </a>
                      </LinkedValue>
                    </FieldGroup>
                  </FieldRow>
                )}
              </Section>

              {/* OTA情報 */}
              <Section>
                <SectionTitle>OTA情報</SectionTitle>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>
                      チャネルマネージャー<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <Select
                      name="channel_manager"
                      options={channelManagerOptions}
                      value={reservation.channel_manager || 'airbnb'}
                      onChangeHandler={handleChange('channel_manager')}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>
                      販売元<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <Select
                      name="seller"
                      options={sellerOptions}
                      value={reservation.seller || 'airbnb'}
                      onChangeHandler={handleChange('seller')}
                    />
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>OTA予約ID</FieldLabel>
                    <InputText
                      name="seller_booking_id"
                      value={reservation.seller_booking_id || ''}
                      onChangeHandler={handleChange('seller_booking_id')}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>
                      処理アクション<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <Select
                      name="process_action"
                      options={OTA_PENDING_PROCESS_ACTION_OPTIONS}
                      value={reservation.process_action || 'new'}
                      onChangeHandler={handleChange('process_action')}
                    />
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup full={true}>
                    <FieldLabel>メールメッセージID</FieldLabel>
                    <ReadOnlyValue>{reservation.email_message_id || '-'}</ReadOnlyValue>
                  </FieldGroup>
                </FieldRow>
              </Section>

              {/* 顧客情報 */}
              <Section>
                <SectionTitle>顧客情報</SectionTitle>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>名</FieldLabel>
                    <InputText
                      name="customer_first_name"
                      value={reservation.customer_first_name || ''}
                      onChangeHandler={handleChange('customer_first_name')}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>姓</FieldLabel>
                    <InputText
                      name="customer_last_name"
                      value={reservation.customer_last_name || ''}
                      onChangeHandler={handleChange('customer_last_name')}
                    />
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>メールアドレス</FieldLabel>
                    <InputText
                      name="customer_email"
                      value={reservation.customer_email || ''}
                      onChangeHandler={handleChange('customer_email')}
                    />
                    {errors.customer_email && (
                      <ErrorText>{errors.customer_email.join(', ')}</ErrorText>
                    )}
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>電話番号</FieldLabel>
                    <InputText
                      name="customer_phone"
                      value={reservation.customer_phone || ''}
                      onChangeHandler={handleChange('customer_phone')}
                    />
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>国</FieldLabel>
                    <InputText
                      name="customer_country"
                      value={reservation.customer_country || ''}
                      onChangeHandler={handleChange('customer_country')}
                    />
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>大人</FieldLabel>
                    <InputText
                      name="num_of_adults"
                      inputMode="numeric"
                      value={String(reservation.num_of_adults || 0)}
                      onChangeHandler={handleChange('num_of_adults')}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>子供</FieldLabel>
                    <InputText
                      name="num_of_children"
                      inputMode="numeric"
                      value={String(reservation.num_of_children || 0)}
                      onChangeHandler={handleChange('num_of_children')}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>幼児</FieldLabel>
                    <InputText
                      name="num_of_infants"
                      inputMode="numeric"
                      value={String(reservation.num_of_infants || 0)}
                      onChangeHandler={handleChange('num_of_infants')}
                    />
                  </FieldGroup>
                </FieldRow>
                {errors.num_of_people && <ErrorText>{errors.num_of_people.join(', ')}</ErrorText>}
              </Section>

              {/* ツアー情報 */}
              <Section>
                <SectionTitle>ツアー情報</SectionTitle>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>
                      商品ID<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <InputText
                      name="tour_product_id"
                      value={reservation.tour_product_id || ''}
                      onChangeHandler={handleChange('tour_product_id')}
                      placeholder="21490P..."
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>
                      OTAツアータイトル<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <InputText
                      name="tour_title"
                      value={reservation.tour_title || ''}
                      onChangeHandler={handleChange('tour_title')}
                      placeholder="Make your own sushi at Tsukiji Fish Market"
                    />
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>
                      ツアー日<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <InputText
                      name="tour_date"
                      type="date"
                      value={reservation.tour_date || ''}
                      onChangeHandler={handleChange('tour_date')}
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>
                      開始時刻<RequiredMark>必須</RequiredMark>
                    </FieldLabel>
                    <TimeSelectRow>
                      <Select
                        name="start_hour"
                        options={HOUR_OPTIONS}
                        value={startHour}
                        onChangeHandler={e => setStartHour(e.target.value)}
                      />
                      <TimeColon>:</TimeColon>
                      <Select
                        name="start_minute"
                        options={MINUTE_OPTIONS}
                        value={startMinute}
                        onChangeHandler={e => setStartMinute(e.target.value)}
                      />
                    </TimeSelectRow>
                  </FieldGroup>
                </FieldRow>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>通貨</FieldLabel>
                    <ReadOnlyValue>JPY</ReadOnlyValue>
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>合計金額</FieldLabel>
                    <InputText
                      name="total_price"
                      inputMode="numeric"
                      value={String(reservation.total_price || 0)}
                      onChangeHandler={handleChange('total_price')}
                    />
                  </FieldGroup>
                </FieldRow>
              </Section>

              {/* その他情報 */}
              <Section>
                <SectionTitle>その他情報</SectionTitle>
                <FieldRow>
                  <FieldGroup full={true}>
                    <FieldLabel>Other Requests</FieldLabel>
                    <InputTextArea
                      name="other_requests"
                      value={reservation.other_requests || ''}
                      onChangeHandler={handleChange('other_requests')}
                    />
                  </FieldGroup>
                </FieldRow>
              </Section>

              {/* メタ情報 */}
              <Section>
                <SectionTitle>メタ情報</SectionTitle>
                <FieldRow>
                  <FieldGroup>
                    <FieldLabel>作成日時</FieldLabel>
                    <ReadOnlyValue>{reservation.created_at || '-'}</ReadOnlyValue>
                  </FieldGroup>
                  <FieldGroup>
                    <FieldLabel>更新日時</FieldLabel>
                    <ReadOnlyValue>{reservation.updated_at || '-'}</ReadOnlyValue>
                  </FieldGroup>
                </FieldRow>
                {reservation.processed_at && (
                  <FieldRow>
                    <FieldGroup>
                      <FieldLabel>処理日時</FieldLabel>
                      <ReadOnlyValue>{reservation.processed_at}</ReadOnlyValue>
                    </FieldGroup>
                  </FieldRow>
                )}
              </Section>

              {/* アクションボタン */}
              <ActionSection>
                {errors.base && <ErrorText>{errors.base.join(', ')}</ErrorText>}
                <ButtonGroup>
                  <Button handleClick={handleBack}>戻る</Button>
                  <Button primary={true} handleClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? '保存中...' : '保存'}
                  </Button>
                </ButtonGroup>
                {canProcess && (
                  <ProcessButtonGroup>
                    <Button
                      primary={true}
                      handleClick={handleProcess}
                      disabled={isProcessing || isDirty}
                    >
                      {isProcessing ? '処理中...' : '処理実行（OTA予約作成）'}
                    </Button>
                    {showUnsavedNotice && (
                      <UnsavedNotice>
                        未保存の変更があります。保存後に処理してください。
                      </UnsavedNotice>
                    )}
                  </ProcessButtonGroup>
                )}
              </ActionSection>
            </FormWrapper>
          }
        />
      </Wrapper>
    </SidebarLayout>
  )
}

const StatusBadge = styled.span<{ status: number }>`
  display: inline-block;
  padding: 4px 12px;
  margin-left: 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  background-color: ${({ status }) => getStatusBadgeBackgroundColor(status)};
  color: ${({ status }) => getStatusBadgeTextColor(status)};
`

const Wrapper = styled.div`
  .Index_Main {
    padding: 0 !important;

    .Panel {
      padding: 20px 40px !important;
      overflow: visible;
    }
  }
`

const Heading = styled.div`
  display: flex;
  align-items: center;
`

const FormWrapper = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 24px;
`

const Section = styled.div`
  margin-bottom: 32px;
  padding: 20px;
  background-color: ${constants.COLORS.white};
  border: 1px solid ${constants.COLORS.divider};
  border-radius: 8px;
`

const SectionTitle = styled.h3`
  margin: 0 0 16px;
  padding-bottom: 8px;
  font-size: 16px;
  font-weight: bold;
  color: ${constants.COLORS.black};
  border-bottom: 2px solid ${constants.COLORS.primary};
`

const FieldRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`

const FieldGroup = styled.div<{ full?: boolean }>`
  flex: ${({ full }) => (full ? '1 1 100%' : '1 1 0')};
  min-width: 0;
`

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: bold;
  color: ${constants.COLORS.subText};
`

const RequiredMark = styled.span`
  margin-left: 6px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: normal;
  color: ${constants.COLORS.white};
  background-color: ${constants.COLORS.danger};
  border-radius: 10px;
`

const ReadOnlyValue = styled.div`
  padding: 8px 12px;
  background-color: ${constants.COLORS.background};
  border: 1px solid ${constants.COLORS.divider};
  border-radius: 4px;
  font-size: 14px;
  color: ${constants.COLORS.black};
`

const LinkedValue = styled.div`
  padding: 8px 12px;
  background-color: ${constants.COLORS.background};
  border: 1px solid ${constants.COLORS.divider};
  border-radius: 4px;
  font-size: 14px;

  a {
    color: ${constants.COLORS.primary};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`

const ErrorText = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: ${constants.COLORS.danger};
`

const ActionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
  background-color: ${constants.COLORS.white};
  border: 1px solid ${constants.COLORS.divider};
  border-radius: 8px;
`

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;

  button {
    min-width: 120px;
  }
`

const ProcessButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  padding-top: 16px;
  border-top: 1px solid ${constants.COLORS.divider};

  button {
    min-width: 200px;
    background-color: #4caf50;

    &:hover {
      background-color: #388e3c;
    }

    &:disabled {
      background-color: #388e3c !important;
      border-color: #388e3c !important;
      cursor: not-allowed;
    }
  }
`

const UnsavedNotice = styled.div`
  font-size: 12px;
  color: ${constants.COLORS.danger};
  text-align: center;
`

const TimeSelectRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const TimeColon = styled.span`
  font-size: 16px;
  font-weight: bold;
`

export default AgentOtaPendingReservationEdit
